import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { SERVICES } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", phone: "", email: "", service: "", message: "" };

const inputClass =
  "w-full rounded-xl border border-white/15 bg-[#163B2C]/60 px-4 py-3 text-sm text-[#F6F4EE] placeholder:text-[#F6F4EE]/40 outline-none transition-colors duration-300 focus:border-[#22C55E] focus:bg-[#163B2C]";

export const ContactForm = ({ compact = false }) => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, service: form.service || null });
      toast.success("Request received — we'll call you back shortly.");
      setForm(initialForm);
    } catch {
      toast.error("Something went wrong. Please call us at 778-823-2000.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-4">
      <div className={compact ? "space-y-4" : "grid gap-4 sm:grid-cols-2"}>
        <input
          data-testid="contact-name-input"
          required
          value={form.name}
          onChange={update("name")}
          placeholder="Your name"
          className={inputClass}
        />
        <input
          data-testid="contact-phone-input"
          required
          value={form.phone}
          onChange={update("phone")}
          placeholder="Phone number"
          type="tel"
          className={inputClass}
        />
      </div>
      <div className={compact ? "space-y-4" : "grid gap-4 sm:grid-cols-2"}>
        <input
          data-testid="contact-email-input"
          required
          value={form.email}
          onChange={update("email")}
          placeholder="Email address"
          type="email"
          className={inputClass}
        />
        <select
          data-testid="contact-service-select"
          value={form.service}
          onChange={update("service")}
          className={`${inputClass} appearance-none ${form.service ? "" : "text-[#F6F4EE]/40"}`}
        >
          <option value="" className="bg-[#163B2C]">
            Service needed (optional)
          </option>
          {SERVICES.map((s) => (
            <option key={s.id} value={s.title} className="bg-[#163B2C] text-[#F6F4EE]">
              {s.title}
            </option>
          ))}
          <option value="Other" className="bg-[#163B2C] text-[#F6F4EE]">
            Other / General labour
          </option>
        </select>
      </div>
      <textarea
        data-testid="contact-message-input"
        required
        value={form.message}
        onChange={update("message")}
        placeholder="Tell us about the job — crop, location, crew size, dates…"
        rows={compact ? 4 : 5}
        className={`${inputClass} resize-none`}
      />
      <button
        data-testid="contact-submit-button"
        type="submit"
        disabled={sending}
        className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#22C55E] px-8 py-4 text-sm font-semibold text-[#0F291E] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 sm:w-auto"
      >
        {sending ? "Sending…" : "Send Request"}
        <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};
